import React from 'react';
import { BiLayer, BiUndo, BiRedo, BiCube, BiSave, BiRefresh } from 'react-icons/bi';
import { useTranslation } from '../../utils/languageUtils';

// Toolbox actions: supports inline (mobile) and floating (desktop) modes
// New selection-aware visibility:
// - selectedNodeType: optional prop ('text' | 'image' | null). If not provided, we listen to global SELECTION_CHANGED events.
// - buttonModes: optional map to control per-button visibility based on node type. Values: 'all' | 'text' | 'image' | 'none'.
//   Keys: save, undo, redo, threeD, layer, reset
//   Example: { reset: 'image', layer: 'text' }
const ToolboxTab = ({
  onShowLayerModal,
  onCloseLayerModal,
  onToggle3D,
  onAddToCart,
  mode = 'inline',
  show3D = true,
  konvaController,
  isThreeDActive,
  isLayerOpen,
  configData,
  selectedNodeType: selectedNodeTypeProp = null,
  buttonModes = {},
}) => {
  const { t } = useTranslation();
  // Local state fallbacks in case parent doesn't pass active flags
  const [localThreeDActive, setLocalThreeDActive] = React.useState(!!isThreeDActive);
  const [localLayerOpen, setLocalLayerOpen] = React.useState(!!isLayerOpen);
  const [hintVisible, setHintVisible] = React.useState(true);

  // Keep locals in sync if props are controlled from parent
  React.useEffect(() => { if (typeof isThreeDActive === 'boolean') setLocalThreeDActive(isThreeDActive); }, [isThreeDActive]);
  React.useEffect(() => { if (typeof isLayerOpen === 'boolean') setLocalLayerOpen(isLayerOpen); }, [isLayerOpen]);

  // Track current node selection type for button visibility
  const [selectedNodeType, setSelectedNodeType] = React.useState(() => {
    try {
      if (selectedNodeTypeProp) return selectedNodeTypeProp;
      if (typeof window !== 'undefined' && window.selectedNodeType) return window.selectedNodeType;
    } catch {}
    return null; // none/unknown
  });

  const inferNodeTypeFromNode = React.useCallback((node) => {
    try {
      if (!node) return null;
      const cn = node.className || (typeof node.getType === 'function' ? node.getType() : null);
      if (cn === 'Text') return 'text';
      if (cn === 'Image') return 'image';
      // Fallback: treat anything with an image() function or name containing image/sticker as image
      try {
        const nm = typeof node.name === 'function' ? node.name() : (node.attrs && node.attrs.name);
        if (nm && typeof nm === 'string') {
          const low = nm.toLowerCase();
          if (low.includes('image') || low.includes('sticker')) return 'image';
          if (low.includes('text')) return 'text';
        }
      } catch {}
      return null;
    } catch {
      return null;
    }
  }, []);

  const effectiveSelectedNodeType = React.useMemo(() => {
    // If parent provides selection type, trust it.
    if (selectedNodeTypeProp != null) return selectedNodeTypeProp;
    // If state has it (from SELECTION_CHANGED), use it.
    if (selectedNodeType) return selectedNodeType;

    // Otherwise infer from actual selection/transformer state.
    try {
      const nodeFromController = konvaController && typeof konvaController.getSelectedNode === 'function'
        ? konvaController.getSelectedNode()
        : null;
      const node = nodeFromController || (typeof window !== 'undefined' ? window.selectedKonvaNode : null);
      return inferNodeTypeFromNode(node);
    } catch {}
    return null;
  }, [selectedNodeTypeProp, selectedNodeType, konvaController, inferNodeTypeFromNode]);

  // Keep internal selection in sync with prop if provided
  React.useEffect(() => {
    if (selectedNodeTypeProp !== undefined) {
      setSelectedNodeType(selectedNodeTypeProp ?? null);
    }
  }, [selectedNodeTypeProp]);

  // Listen for global events broadcast by ProductView to unify state across instances
  React.useEffect(() => {
    const on3DState = (e) => {
      if (e && e.detail && typeof e.detail.active === 'boolean') {
        setLocalThreeDActive(e.detail.active);
      }
    };
    const onLayerOpen = () => setLocalLayerOpen(true);
    const onLayerClose = () => setLocalLayerOpen(false);
    const onViewChanged = () => {
      // When view changes, the layer modal auto closes and 3D preview may close.
      // Reset button states to reflect the UI.
      setLocalLayerOpen(false);
      setLocalThreeDActive(false);
    };
    const onSelectionChanged = (e) => {
      try {
        const nt = e && e.detail ? e.detail.nodeType : null; // 'text' | 'image' | null
        setSelectedNodeType(nt ?? null);
        if (typeof window !== 'undefined') window.selectedNodeType = nt ?? null;
      } catch {}
    };
    const onHintVisibilityChanged = (e) => {
      try {
        const v = e && e.detail ? e.detail.visible : undefined;
        if (typeof v === 'boolean') {
          setHintVisible(v);
        }
      } catch {}
    };
    window.addEventListener('THREED_STATE', on3DState);
    window.addEventListener('LAYER_MODAL_OPEN', onLayerOpen);
    window.addEventListener('LAYER_MODAL_CLOSE', onLayerClose);
    window.addEventListener('VIEW_CHANGED', onViewChanged);
    window.addEventListener('SELECTION_CHANGED', onSelectionChanged);
    window.addEventListener('HINT_VISIBILITY_CHANGED', onHintVisibilityChanged);

    // Initialize from cached global state if available
    try {
      if (typeof window !== 'undefined' && typeof window.__hintVisible === 'boolean') {
        setHintVisible(window.__hintVisible);
      }
    } catch {}
    return () => {
      window.removeEventListener('THREED_STATE', on3DState);
      window.removeEventListener('LAYER_MODAL_OPEN', onLayerOpen);
      window.removeEventListener('LAYER_MODAL_CLOSE', onLayerClose);
      window.removeEventListener('VIEW_CHANGED', onViewChanged);
      window.removeEventListener('SELECTION_CHANGED', onSelectionChanged);
      window.removeEventListener('HINT_VISIBILITY_CHANGED', onHintVisibilityChanged);
    };
  }, []);
  const handleOpenLayer = () => {
    console.log('[ToolboxTab] Layer button clicked');
    const effectiveOpen = (typeof isLayerOpen === 'boolean' ? isLayerOpen : localLayerOpen) === true;
    const threeDActive = (typeof isThreeDActive === 'boolean' ? isThreeDActive : localThreeDActive) === true;

    // If 3D is active, turn it OFF first (mutually exclusive)
    if (threeDActive) {
      if (typeof onToggle3D === 'function') { onToggle3D(); } else { window.dispatchEvent(new CustomEvent('TOGGLE_3D_PREVIEW')); }
      setLocalThreeDActive(false);
    }

    if (effectiveOpen) {
      // Currently open: close it
      if (typeof onCloseLayerModal === 'function') {
        onCloseLayerModal();
      } else {
        try { window.dispatchEvent(new CustomEvent('REQUEST_LAYER_MODAL_TOGGLE')); } catch {}
      }
      setLocalLayerOpen(false);
      return;
    }
    // Currently closed: open it
    if (typeof onShowLayerModal === 'function') {
      onShowLayerModal();
    } else {
      // Fallback: request toggle via global event
      try { window.dispatchEvent(new CustomEvent('REQUEST_LAYER_MODAL_TOGGLE')); } catch {}
    }
    setLocalLayerOpen(true);
  };

  // Responsive sizing for mobile vs desktop
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 768);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Desktop floating toolbar should be vertical
  const isVertical = !isMobile && mode === 'floating';

  // Config-driven visibility: show Save/Undo/Redo only if hisorry_fucntion === true
  const showHistoryButtons = !!(configData && configData.hisorry_fucntion === true);

  const iconSize = isMobile ? 20 : 16;
  const baseBtn = {
    padding: isMobile ? '8px 12px' : '6px 10px',
    fontSize: isMobile ? 14 : 12,
    borderRadius: 6,
    border: '1px solid rgba(0,0,0,0.2)',
    background: '#fff',
    color: '#000',
    cursor: 'pointer'
  };

  const getBtnStyle = (active = false, disabled = false) => ({
    ...baseBtn,
    background: active ? '#000' : '#fff',
    color: active ? '#fff' : '#000',
    borderColor: active ? '#000' : 'rgba(0,0,0,0.2)',
    boxShadow: active ? '0 0 0 2px rgba(0,0,0,0.2)' : 'none',
    transition: 'background-color 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
    opacity: disabled ? 0.7 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer'
  });

  const groupStyle = {
    display: 'inline-flex',
    gap: isMobile ? 6 : 8,
    alignItems: 'center',
    flexWrap: 'nowrap',
    flexDirection: isVertical ? 'column' : 'row'
  };

  // Buttons are interactive in both modes; global events keep UI in sync across instances

  // Selection-aware visibility helpers
  //all , text , image
  const modes = React.useMemo(() => ({
    save: 'all',
    undo: 'all',
    redo: 'all',
    threeD: 'all',
    layer: 'all',
    reset: 'all',
    centerX: ['text', 'image'],
    centerY: ['text', 'image'],
    hint: 'all',
    ...(buttonModes || {}),
  }), [buttonModes]);

  const visibleFor = (modeKey) => {
    const m = modes[modeKey] || 'all';
    if (m === 'all') return true;
    // Show only when no selection
    if (m === 'none') return !effectiveSelectedNodeType;
    // Arrays can include 'none' or specific types
    if (Array.isArray(m)) {
      const wantsNone = m.includes('none');
      if (!effectiveSelectedNodeType) return wantsNone;
      return m.includes(effectiveSelectedNodeType);
    }
    // For 'text' | 'image'
    if (!effectiveSelectedNodeType) return false;
    return m === effectiveSelectedNodeType;
  };

  const handleUndo = () => {
    console.log('[ToolboxTab] Undo button clicked, hasController:', !!konvaController);
    if (konvaController && typeof konvaController.undo === 'function') {
      konvaController.undo();
    } else {
      console.warn('[ToolboxTab] konvaController.undo is not available');
    }
  };
  const handleRedo = () => {
    console.log('[ToolboxTab] Redo button clicked, hasController:', !!konvaController);
    if (konvaController && typeof konvaController.redo === 'function') {
      konvaController.redo();
    } else {
      console.warn('[ToolboxTab] konvaController.redo is not available');
    }
  };

  const handleCenterX = () => {
    try {
      const hasSel = (typeof window !== 'undefined' && window.selectedKonvaNode) || (konvaController && typeof konvaController.getSelectedNode === 'function' && konvaController.getSelectedNode());
      if (!hasSel) {
        console.warn('[ToolboxTab] Center X: no selected node');
        return;
      }
    } catch {}
    if (konvaController && typeof konvaController.centerSelected === 'function') {
      try { konvaController.centerSelected('x'); return; } catch {}
    }
    // Fallback to global event for controller to handle
    window.dispatchEvent(new CustomEvent('CENTER_X'));
  };

  const handleCenterY = () => {
    try {
      const hasSel = (typeof window !== 'undefined' && window.selectedKonvaNode) || (konvaController && typeof konvaController.getSelectedNode === 'function' && konvaController.getSelectedNode());
      if (!hasSel) {
        console.warn('[ToolboxTab] Center Y: no selected node');
        return;
      }
    } catch {}
    if (konvaController && typeof konvaController.centerSelected === 'function') {
      try { konvaController.centerSelected('y'); return; } catch {}
    }
    // Fallback to global event for controller to handle
    window.dispatchEvent(new CustomEvent('CENTER_Y'));
  };
  const handleSave = () => {
    console.log('[ToolboxTab] Save button clicked, hasController:', !!konvaController);
    if (
      konvaController &&
      typeof konvaController.setHistoryState === 'function' &&
      typeof konvaController.getHistoryState === 'function'
    ) {
      const snap = konvaController.getHistoryState();
      console.log('[ToolboxTab] Snapshot length from getHistoryState:', snap ? snap.length : 0);
      konvaController.setHistoryState(snap);
    } else {
      console.warn('[ToolboxTab] konvaController history API not available');
    }
  };
  const handleReset = () => {
    if (!konvaController || typeof konvaController.resetDesign !== 'function') return;
    try {
      const confirmed = typeof window !== 'undefined' ? window.confirm('Clear all added design elements? This cannot be undone.') : true;
      if (!confirmed) return;
    } catch (_) {}
    konvaController.resetDesign();
  };
  const handle3D = () => {
    console.log('[ToolboxTab] 3D View clicked');
    const layerOpen = (typeof isLayerOpen === 'boolean' ? isLayerOpen : localLayerOpen) === true;
    const threeDActive = (typeof isThreeDActive === 'boolean' ? isThreeDActive : localThreeDActive) === true;

    // If Layer modal is open, close it first
    if (layerOpen) {
      if (typeof onCloseLayerModal === 'function') {
        onCloseLayerModal();
      } else {
        try { window.dispatchEvent(new CustomEvent('REQUEST_LAYER_MODAL_TOGGLE')); } catch {}
      }
      setLocalLayerOpen(false);
    }

    // Ensure 3D is ON if it wasn't active; otherwise toggle off if no layer was being closed
    if (!threeDActive) {
      if (typeof onToggle3D === 'function') { onToggle3D(); } else { window.dispatchEvent(new CustomEvent('TOGGLE_3D_PREVIEW')); }
      setLocalThreeDActive(true);
    } else if (!layerOpen) {
      // Normal toggle off when already active and no cross-close happened
      if (typeof onToggle3D === 'function') { onToggle3D(); } else { window.dispatchEvent(new CustomEvent('TOGGLE_3D_PREVIEW')); }
      setLocalThreeDActive(false);
    }
  };
  const handleOpenLayerLocal = () => {
    handleOpenLayer();
  };
  const handleToggleHint = () => {
    const next = !hintVisible;
    setHintVisible(next);
    try {
      if (konvaController && typeof konvaController.setHintVisible === 'function') {
        konvaController.setHintVisible(next);
      } else {
        window.dispatchEvent(new CustomEvent('HINT_LAYER_TOGGLE', { detail: { visible: next } }));
      }
    } catch (_) {}
  };
  // Removed Done action from toolbox per design change

  return (
    <div
      className="toolbox-tab"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: mode === 'floating' ? 'center' : 'space-between',
        flexDirection: isVertical ? 'column' : 'row',
        padding: mode === 'floating' ? 6 : 0,
        margin: 0,
        width: mode === 'floating' ? 'auto' : '100%',
        // Improve readability when floating over images/dark areas
        background: mode === 'floating' ? 'var(--toolbar-bg, rgba(255,255,255,0.85))' : 'transparent',
        border: mode === 'floating' ? '1px solid var(--site-border, rgba(0,0,0,0.15))' : 'none',
        borderRadius: mode === 'floating' ? 10 : 0,
        boxShadow: mode === 'floating' ? '0 4px 16px rgba(0,0,0,0.12)' : 'none',
        backdropFilter: mode === 'floating' ? 'saturate(180%) blur(6px)' : 'none'
      }}
    >
      {showHistoryButtons && (
        <div style={groupStyle}>
          {visibleFor('save') && (
            <button type="button" data-mode={modes.save} onClick={handleSave} className="tool-btn" style={getBtnStyle(false)} title={t('general.save')} aria-label={t('general.save')} aria-pressed={false}>
              <BiSave size={iconSize} />
            </button>
          )}
          {visibleFor('undo') && (
            <button type="button" data-mode={modes.undo} onClick={handleUndo} className="tool-btn" style={getBtnStyle(false)} title={t('general.undo')} aria-label={t('general.undo')} aria-pressed={false}>
              <BiUndo size={iconSize} />
            </button>
          )}
          {visibleFor('redo') && (
            <button type="button" data-mode={modes.redo} onClick={handleRedo} className="tool-btn" style={getBtnStyle(false)} title={t('general.redo')} aria-label={t('general.redo')} aria-pressed={false}>
              <BiRedo size={iconSize} />
            </button>
          )}
        </div>
      )}
      <div style={{ ...groupStyle, marginTop: isVertical && showHistoryButtons ? 8 : 0 }}>
        {show3D && visibleFor('threeD') && (
          <button
            type="button"
            data-mode={modes.threeD}
            onClick={handle3D}
            className={`tool-btn ${(typeof isThreeDActive === 'boolean' ? isThreeDActive : localThreeDActive) ? 'active' : ''}`}
            style={getBtnStyle((typeof isThreeDActive === 'boolean' ? isThreeDActive : localThreeDActive), false)}
            title={t('general.view3d')}
            aria-label={t('general.view3d')}
            aria-pressed={(typeof isThreeDActive === 'boolean' ? isThreeDActive : localThreeDActive)}
          >
            <BiCube size={iconSize} />
          </button>
        )}
        {visibleFor('centerX') && (
          <button
            type="button"
            data-mode={Array.isArray(modes.centerX) ? modes.centerX.join(',') : modes.centerX}
            onClick={handleCenterX}
            className="tool-btn"
            style={getBtnStyle(false)}
            title={t('general.centerX', 'Center X')}
            aria-label={t('general.centerX', 'Center X')}
            aria-pressed={false}
          >
            {/* Inline SVG icon: center horizontally */}
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" aria-hidden="true">
              <g transform="rotate(90 12 12)">
                <path
                  d="M12.501 14.792l3.854 3.854-.707.707L13 16.705V23h-1v-6.293l-2.646 2.646-.707-.707zM8.647 6.354l3.854 3.854 3.854-3.854-.707-.707L13 8.295V2h-1v6.293L9.354 5.647zM6 13h13v-1H6z"
                  fill="currentColor"
                />
              </g>
            </svg>
          </button>
        )}
        {visibleFor('centerY') && (
          <button
            type="button"
            data-mode={Array.isArray(modes.centerY) ? modes.centerY.join(',') : modes.centerY}
            onClick={handleCenterY}
            className="tool-btn"
            style={getBtnStyle(false)}
            title={t('general.centerY', 'Center Y')}
            aria-label={t('general.centerY', 'Center Y')}
            aria-pressed={false}
          >
            {/* Inline SVG icon: center vertically */}
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12.501 14.792l3.854 3.854-.707.707L13 16.705V23h-1v-6.293l-2.646 2.646-.707-.707zM8.647 6.354l3.854 3.854 3.854-3.854-.707-.707L13 8.295V2h-1v6.293L9.354 5.647zM6 13h13v-1H6z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
        {visibleFor('layer') && (
          <button
            type="button"
            data-mode={modes.layer}
            onClick={handleOpenLayerLocal}
            className={`tool-btn ${(typeof isLayerOpen === 'boolean' ? isLayerOpen : localLayerOpen) ? 'active' : ''}`}
            style={getBtnStyle((typeof isLayerOpen === 'boolean' ? isLayerOpen : localLayerOpen), false)}
            title={t('general.layer')}
            aria-label={t('general.layer')}
            aria-pressed={(typeof isLayerOpen === 'boolean' ? isLayerOpen : localLayerOpen)}
          >
            <BiLayer size={iconSize} />
          </button>
        )}
        {visibleFor('reset') && (
          <button
            type="button"
            data-mode={modes.reset}
            onClick={handleReset}
            className="tool-btn"
            style={getBtnStyle(false)}
            title={t('general.reset') || 'Reset'}
            aria-label={t('general.reset') || 'Reset'}
            aria-pressed={false}
          >
            <BiRefresh size={iconSize} />
          </button>
        )}
        {visibleFor('hint') && (
          <button
            type="button"
            data-mode={modes.hint}
            onClick={handleToggleHint}
            className={`tool-btn ${hintVisible ? 'active' : ''}`}
            style={getBtnStyle(hintVisible, false)}
            title={t('general.hint', 'Hint Area')}
            aria-label={t('general.hint', 'Hint Area')}
            aria-pressed={hintVisible}
          >
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5c-4.5 0-8.2 2.9-10 7 1.8 4.1 5.5 7 10 7s8.2-2.9 10-7c-1.8-4.1-5.5-7-10-7z" fill="currentColor" opacity="0.7" />
              <circle cx="12" cy="12" r="3.5" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ToolboxTab;
